<template>
  <div>
    <v-snackbar v-model="snackbarShown" :multi-line="false">
      {{ snackbarMessage }}
      <v-btn color="primary" text @click="hideSnackbar">{{ $t('close').toUpperCase() }}</v-btn>
    </v-snackbar>
    <v-card class="lesson-resource-dialog pb-8">
      <v-card-title v-if="withCloseButton" primary-title>
        <v-spacer></v-spacer>
        <v-btn outlined small fab @click="close">
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </v-card-title>
      <div class="lesson-resource-dialog-inner">
        <v-card-title>
          <h2 class="mb-5">{{ $t('files') }}</h2>
          <v-tooltip top>
            <template v-slot:activator="{ on }">
              <v-icon v-on="on" class="tooltip">mdi-information-outline</v-icon>
            </template>
            <p v-html="$t('filesAlertPdf')"></p>
          </v-tooltip>
        </v-card-title>
        <v-row no-gutters justify="start">
          <v-col v-for="(file, index) in media.files" :key="index" class="file" md="3" sm="6">
            <div
              :class="[{'file__box--pdf' : isPdf(file.name)},
              {'file__box--img' : isImage(file.name)}]"
              class="file__box"
            >
              <div class="file__box-hover">
                <v-tooltip top>
                  <template v-slot:activator="{ on }">
                    <a :href="file.link" target="_blank">
                      <v-btn class="mr-1" v-on="on" depressed small fab>
                        <v-icon>mdi-download</v-icon>
                      </v-btn>
                    </a>
                  </template>
                  <span>{{ $t('download') }}</span>
                </v-tooltip>
                <v-tooltip top>
                  <template v-slot:activator="{ on }">
                    <v-btn
                      class="ml-1"
                      depressed
                      small
                      fab
                      v-on="on"
                      @click="removeFile(file)"
                      v-if="isTeacher"
                    >
                      <v-icon>mdi-delete</v-icon>
                    </v-btn>
                  </template>
                  <span>{{ $t('dialogDelete') }}</span>
                </v-tooltip>
              </div>
            </div>
            <p class="file__description mt-3">{{ file.name }}</p>
          </v-col>
          <v-col md="3" sm="6" class="file-upload" v-if="isTeacher">
            <div
              class="files-upload"
              @click="$refs.inputUpload.click()"
              v-if="media.files.length < 8"
            >
              <v-btn depressed small fab :loading="isLoading">
                <v-icon>mdi-cloud-upload</v-icon>
              </v-btn>
              <small>{{ $t('filesAdd') }}</small>
              <input
                ref="inputUpload"
                v-show="false"
                accept="application/pdf, image/*"
                type="file"
                @change="uploadFile"
              />
            </div>
          </v-col>
          <v-col md="3" sm="6" class="file-blank" />
          <v-col md="3" sm="6" class="file-blank" />
        </v-row>

        <v-card-title>
          <h2 class="mt-8">{{ $t('filesUrl') }}</h2>
        </v-card-title>

        <v-list v-if="media.links.length" dense>
          <v-list-item three-line>
            <v-list-item-content>
              <v-list-item-title v-for="(link, index) in media.links" :key="index">
                <a :href="link" class="link" target="_blank">
                  <v-row class="row">
                    <media-icon class="pe-2" :link="link"></media-icon>
                    <strong class="book link__name">{{ truncate(link, {length: 90}) }}</strong>
                  </v-row>

                  <v-btn
                    v-if="isTeacher"
                    class="link__remove"
                    depressed
                    x-small
                    fab
                    @click.stop.prevent="removeLink(index)"
                  >
                    <v-icon>mdi-delete</v-icon>
                  </v-btn>
                </a>
              </v-list-item-title>
            </v-list-item-content>
          </v-list-item>
        </v-list>

        <v-card-actions>
          <v-form v-if="isTeacher" @submit.prevent="addLink">
            <v-text-field
              v-model="newLink"
              type="url"
              ref="NewLinkFormInput"
              :label="$t('filesAddUrl')"
              outlined
            ></v-text-field>
            <v-btn
              :disabled="!isNewLinkFormValid"
              color="primary"
              depressed
              small
              fab
              @click="addLink"
            >
              <v-icon>mdi-plus</v-icon>
            </v-btn>
          </v-form>
        </v-card-actions>
      </div>
    </v-card>
  </div>
</template>

<script>
import { mapGetters } from 'vuex';
import truncate from 'lodash/truncate';
import mediaIcon from '@/components/MediaIcon.vue';

export default {
  components: {
    mediaIcon,
  },
  props: {
    lessonId: {},
    withCloseButton: {},
    media: {
      type: Object,
      required: true,
    },
    windows: {},
  },
  data: () => ({
    isLoading: false,
    newLink: '',
    newLinkRules: [v => !!v || this.$t('filesFillUrl')],
    snackbarShown: false,
    snackbarMessage: '',
  }),
  computed: {
    ...mapGetters(['auth']),
    isNewLinkFormValid() {
      return !!this.newLink;
    },
    isTeacher() {
      return !this.$route.params.viewer;
    },
  },
  methods: {
    truncate,
    isImage(filename) {
      const formats = ['jpg', 'png', 'jpeg', 'heic', 'gif'];
      return formats.some(format => filename.toLowerCase().endsWith(format));
    },
    isPdf(filename) {
      return filename.toLowerCase().endsWith('pdf');
    },
    close() {
      this.$emit('close');
    },
    uploadFile(event) {
      const { files } = event.target;
      const file = files[0];
      const filetype = file.name.split('.').slice(-1)[0];
      const shouldUpdateKeynoteWindow = this.media.files.length === 0 || this.media.links.length;
      const allowedFiletypes = ['jpg', 'png', 'jpeg', 'heic', 'gif', 'pdf'];
      const sizeInMb = file.size / 1024 / 1024;
      if (sizeInMb < 10) {
        if (allowedFiletypes.some(allowedFiletype => filetype.toLowerCase() === allowedFiletype)) {
          this.isLoading = true;
          this.$store
            .dispatch('uploadFile', {
              file,
              lesson: this.lessonId || this.$route.params.lesson,
              userId: this.auth.uid,
              currentMedia: this.media,
              errorCallback: () => {
                this.showSnackbar(this.$t('fileUploadFailed'));
              },
            })
            .then(link => {
              this.isLoading = false;
              if (shouldUpdateKeynoteWindow) this.updateKeynoteWindow(link);
              this.$store.dispatch('logEvent', {
                name: 'file_uploaded',
              });
            });
        } else {
          this.showSnackbar(this.$t('filetypeNotAllowed'));
        }
      } else {
        this.showSnackbar(this.$t('filesMaxSize'));
      }
    },

    hideSnackbar() {
      this.snackbarShown = false;
      this.snackbarMessage = '';
    },

    showSnackbar(message) {
      this.snackbarShown = true;
      this.snackbarMessage = message;
      setTimeout(() => {
        this.hideSnackbar();
      }, 3000);
    },

    removeFile(file) {
      this.$store.dispatch('removeFile', {
        filename: file.name,
        lesson: this.lessonId || this.$route.params.lesson,
        email: this.auth.email,
        currentMedia: this.media,
      });
    },

    addLink() {
      const shouldUpdateKeynoteWindow = this.media.files.length === 0 || this.media.links.length;
      if (this.isNewLinkFormValid) {
        let link = this.newLink;

        if (!link.includes('http')) {
          link = `http://${link}`;
        }

        this.media.links.push(link);
        this.newLink = '';
        this.$refs.NewLinkFormInput.focus();
        this.updateLinks();
        this.$store.dispatch('logEvent', {
          name: 'link_added',
        });
        if (shouldUpdateKeynoteWindow) this.updateKeynoteWindow(link);
      }
    },
    removeLink(index) {
      this.media.links = this.media.links.filter((v, i) => i !== index);
      this.updateLinks();
    },
    updateLinks() {
      this.$store.dispatch('updateDb', {
        type: 'lessons',
        id: this.lessonId || this.$route.params.lesson,
        item: 'media.links',
        value: this.media.links,
      });
    },
    updateKeynoteWindow(link) {
      this.$store.dispatch('updateDb', {
        type: 'lessons',
        id: this.lessonId || this.$route.params.lesson,
        item: 'windows',
        value: [this.windows[0], { type: 'keynote', page: 1, link }],
      });
    },
  },
};
</script>

<style lang="scss">
.tooltip {
  margin-top: -16px;
  padding-left: 8px;
  opacity: 0.4;
}
.lesson-resource-dialog {
  &-inner {
    max-width: 825px;
    margin: 0 auto;
  }

  .file-blank {
    height: 0;
  }

  .file {
    &__description {
      text-align: center;
      font-size: 14px;
    }

    &__box {
      background-size: 48px auto;
      background-position: center;
      border-radius: 10px;
      width: 180px;
      height: 136px;
      margin: 0 auto;
      cursor: pointer;

      display: flex;
      align-items: center;
      justify-content: center;

      &--pdf {
        background-color: rgba(#f98786, 0.15);
        background-image: url('/img/icon-file-pdf.png');
      }

      &--img {
        background-color: rgba(#52c732, 0.15);
        background-image: url('/img/icon-file-img.png');
      }

      &:not(:hover) {
        .file__box-hover {
          visibility: hidden;
          opacity: 0;
        }
      }

      &-hover {
        display: none;
        background: rgba(#17173a, 0.6);
        border-radius: 10px;
        width: 100%;
        height: 100%;
        transition: visibility 0s, opacity 0.2s linear;
        visibility: visible;
        opacity: 1;

        display: flex;
        align-items: center;
        justify-content: center;

        a {
          text-decoration: none;
        }
      }
    }
  }

  .file-upload {
    @media (max-width: 600px) {
      min-width: 100%;
      width: 100%;
    }
  }

  .files-upload {
    border: 1px dashed #979797;
    border-radius: 6px;
    width: 180px;
    height: 136px;
    margin: 0 auto;
    display: flex;
    justify-content: center;
    align-items: center;
    flex-direction: column;

    cursor: pointer;

    &:hover {
      border-color: #0091ff;
    }
  }

  .v-list-item {
    padding: 0;
  }

  .link {
    cursor: pointer;
    text-decoration: none;
    background: none;

    border: 1px solid transparent;
    border-radius: 6px;
    display: block;
    color: #17173a;

    padding-top: 8px;
    padding-bottom: 8px;
    padding-left: 16px;
    padding-right: 16px;

    display: flex;
    align-items: center;
    justify-content: space-between;

    transition: all 0.2s ease;

    &:not(:hover) {
      .link {
        &__remove {
          opacity: 0;
          pointer-events: none;
        }
        &__name {
          max-width: 50%;
        }
      }
    }

    &:hover {
      background: rgba(#d8d8d8, 0.15);
      border-color: rgba(#979797, 0.15);
    }
  }

  .v-form {
    width: 100%;
    position: relative;

    .v-text-field--outlined fieldset {
      border-style: dashed;
    }

    .v-btn {
      position: absolute !important;
      right: 10px !important;
      top: 8px !important;
    }
  }
}
</style>
